import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { cn } from "@src/lib/utils/cn";

// Vertical rhythm between markdown blocks. "comfortable" is the legacy Ask
// Opus chat's spacing (unchanged); "dense" is roughly half of it for the Opus
// Agent drawer, where a long answer should read as one compact block — no
// double gap around lists (list-item margin folded into the list's space-y).
type BlockSpacing = Record<
  | "p"
  | "h1"
  | "h2"
  | "h3"
  | "list"
  | "li"
  | "pre"
  | "blockquote"
  | "hr"
  | "table",
  string
>;

const BLOCK_SPACING: Record<"comfortable" | "dense", BlockSpacing> = {
  comfortable: {
    p: "mb-4 last:mb-0",
    h1: "mt-6 mb-4",
    h2: "mt-5 mb-3",
    h3: "mt-4 mb-2",
    list: "mb-4 space-y-1",
    li: "mb-1",
    pre: "mb-4",
    blockquote: "my-4",
    hr: "my-6",
    table: "mb-4",
  },
  dense: {
    p: "mb-2 last:mb-0",
    h1: "mt-3 mb-2",
    h2: "mt-2.5 mb-1.5",
    h3: "mt-2 mb-1",
    list: "mb-2 space-y-0.5",
    li: "mb-0",
    pre: "mb-2",
    blockquote: "my-2",
    hr: "my-3",
    table: "mb-2",
  },
};

export interface RenderMarkdownOptions {
  // Tighter block rhythm for the Opus Agent answer surface only.
  dense?: boolean;
}

/**
 * Renders text as markdown without inline citations.
 * Citations are now displayed at the bottom of messages via MessageAnnotations component.
 */
export const renderMarkdown = (
  content: string,
  { dense = false }: RenderMarkdownOptions = {},
): React.ReactNode => {
  const spacing = BLOCK_SPACING[dense ? "dense" : "comfortable"];
  // Simplified to just render markdown without inline citations
  // Citations are now shown at the bottom via MessageAnnotations component
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      className="prose prose-sm max-w-none"
      components={{
        p: ({ children }) => <p className={spacing.p}>{children}</p>,
        h1: ({ children }) => (
          <h1 className={cn(spacing.h1, "text-xl font-semibold first:mt-0")}>
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 className={cn(spacing.h2, "text-lg font-semibold first:mt-0")}>
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className={cn(spacing.h3, "text-base font-semibold first:mt-0")}>
            {children}
          </h3>
        ),
        ul: ({ children }) => (
          <ul className={cn(spacing.list, "list-disc pl-6")}>{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className={cn(spacing.list, "list-decimal pl-6")}>{children}</ol>
        ),
        li: ({ children }) => <li className={spacing.li}>{children}</li>,
        strong: ({ children }) => (
          <strong className="font-semibold">{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children }) => (
          <code className="rounded bg-muted px-1.5 py-0.5 text-sm">
            {children}
          </code>
        ),
        pre: ({ children }) => (
          <pre
            className={cn(
              spacing.pre,
              "overflow-x-auto rounded-lg bg-muted p-4",
            )}
          >
            {children}
          </pre>
        ),
        blockquote: ({ children }) => (
          <blockquote
            className={cn(
              spacing.blockquote,
              "border-l-4 border-muted pl-4 italic",
            )}
          >
            {children}
          </blockquote>
        ),
        hr: () => <hr className={cn(spacing.hr, "border-muted")} />,
        // Chat surfaces render markdown in ~326px drawers where the prose
        // default squeezes table columns until every cell wraps into a
        // vertical letter stack: lay tables out at their natural width and
        // scroll them horizontally instead.
        table: ({ children }) => (
          <div className={cn(spacing.table, "overflow-x-auto")}>
            <table className="w-max min-w-full border-collapse text-sm">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-border-primary bg-bg-primary px-2 py-1 text-left font-semibold">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border-primary px-2 py-1 align-top">
            {children}
          </td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
